import {
  Dropzone,
  FileRejection,
  FileWithPath,
  MIME_TYPES as MANTINE_MIME_TYPES
} from '@mantine/dropzone'
import { ReactNode, RefObject, useMemo } from 'react'
import { MEDIA_UPLOAD_LIMITS } from '@/config/media'
import { DropzoneInstructions } from './DropzoneInstructions'
import classes from './disabledDropzone.module.css'
import { checkBatchSize, checkFileCount } from './helpers'

type ExtendedMimeTypes = typeof MANTINE_MIME_TYPES & {
  readonly mov: 'video/quicktime'
}

export type AcceptedFileTypes = (keyof ExtendedMimeTypes)[]

const MIME_TYPES: ExtendedMimeTypes = {
  ...MANTINE_MIME_TYPES,
  mov: 'video/quicktime'
}

type UploadDropzoneProps = {
  children?: ReactNode
  hidden?: boolean
  disabled?: boolean
  withDisabledStyle?: boolean
  acceptedFileTypes?: AcceptedFileTypes
  showInstructions?: boolean
  activateOnClick?: boolean
  minHeight?: number
  fileLimit?: number
  maxSize?: number
  openRef?: RefObject<(() => void) | null>
  onDrop: (acceptedFiles: File[]) => void
  onReject: (fileRejections: FileRejection[]) => void
}

export const UploadDropzone = ({
  children,
  hidden,
  disabled = false,
  withDisabledStyle = true,
  acceptedFileTypes,
  showInstructions,
  activateOnClick,
  minHeight,
  fileLimit,
  maxSize = MEDIA_UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES,
  openRef,
  onDrop,
  onReject
}: UploadDropzoneProps) => {
  const acceptedMimeTypes = useMemo(() => {
    if (!acceptedFileTypes) {
      // By default, if acceptedFileTypes is not provided, all file types are accepted
      return undefined
    }

    return acceptedFileTypes.map((type) => MIME_TYPES[type]).filter(Boolean)
  }, [acceptedFileTypes])

  const validateFileName = (file: File) => {
    // file name is not always available despite the typing
    if (
      file.name &&
      file.name.length > MEDIA_UPLOAD_LIMITS.MAX_FILE_NAME_LENGTH
    ) {
      return {
        code: 'name-too-long',
        message: 'File name is too long'
      }
    }

    return null
  }

  const handleDropAny = (
    files: FileWithPath[],
    fileRejections: FileRejection[]
  ) => {
    const { files: checkCountAccepted, fileRejections: checkCountRejections } =
      checkFileCount(files, fileLimit)

    const { files: acceptedFiles, fileRejections: checkBatchSizeRejections } =
      checkBatchSize(
        checkCountAccepted,
        MEDIA_UPLOAD_LIMITS.MAX_BATCH_SIZE_BYTES
      )

    if (acceptedFiles.length > 0) {
      onDrop(acceptedFiles)
    }

    const rejectedFiles = [
      ...checkCountRejections,
      ...checkBatchSizeRejections,
      ...fileRejections
    ]

    if (rejectedFiles.length > 0) {
      onReject(rejectedFiles)
    }
  }

  return (
    <Dropzone
      openRef={openRef}
      accept={acceptedMimeTypes}
      maxSize={maxSize}
      disabled={disabled}
      activateOnClick={activateOnClick}
      activateOnKeyboard={false}
      enablePointerEvents={!activateOnClick}
      style={{ display: hidden ? 'none' : 'block' }}
      className={disabled && withDisabledStyle ? classes.disabled : undefined}
      mih={minHeight}
      validator={validateFileName}
      onDropAny={handleDropAny}
      onDrop={() => {}}
    >
      {children}

      {showInstructions && (
        <DropzoneInstructions
          maxFileSize={maxSize}
          maxFilesPerUpload={fileLimit}
          acceptedFileTypes={acceptedFileTypes}
        />
      )}
    </Dropzone>
  )
}
